import type { CheckStatus } from "../../api/types";
import { t } from "../../i18n/t";

/**
 * xmg-webux (first staging trial, defect 4): ONE definition of "a failing check", shared by the sign-off
 * card's note and button, the comparison-point tiles, each check group's summary line and the sign-off
 * dialog. The tiles used to count everything that was not PASS or INFO as "fail" -- WARN rows included --
 * while the sign-off card counted only FAIL and MISSING, so the same dry run read "5 fail" in the tiles and
 * "3 checks fail" beside the button. A WARN is a warning (it has its own tile number), never a failure.
 */
export function isFailingStatus(status: CheckStatus): boolean {
  return status === "FAIL" || status === "MISSING";
}

export interface CheckCounts {
  pass: number;
  fail: number;
  warn: number;
}

export function countChecks(rows: readonly { status: CheckStatus }[]): CheckCounts {
  let pass = 0;
  let fail = 0;
  let warn = 0;
  for (const row of rows) {
    if (row.status === "PASS") pass += 1;
    else if (row.status === "WARN") warn += 1;
    else if (isFailingStatus(row.status)) fail += 1;
  }
  return { pass, fail, warn };
}

/** "424 passed, 3 warnings": the counts a closed section of checks carries in its summary. Zero parts are left out. */
export function describeCounts(counts: CheckCounts): string {
  return [
    counts.pass === 0 ? null : t("dryrun.counts_passed", { n: counts.pass }),
    counts.fail === 0 ? null : t("dryrun.counts_failed", { n: counts.fail }),
    counts.warn === 0
      ? null
      : counts.warn === 1
        ? t("dryrun.counts_warning_one")
        : t("dryrun.counts_warnings", { n: counts.warn }),
  ]
    .filter((part): part is string => part !== null)
    .join(", ");
}
